/*
 * This file is part of eyeo's Web Extension Ad Blocking Toolkit (EWE),
 * Copyright (C) 2006-present eyeo GmbH
 *
 * EWE is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License version 3 as
 * published by the Free Software Foundation.
 *
 * EWE is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with EWE.  If not, see <http://www.gnu.org/licenses/>.
 */

import {MILLIS_IN_HOUR} from "adblockpluscore/lib/time.js";

const MAX_POLL_TIMEOUT = MILLIS_IN_HOUR;

/**
 * Scheduler for managing calling a given function with a large interval, for
 * example daily.
 */
export class Scheduler {
  /**
   * Starts the scheduler with the given configuration.
   *
   * @param {Object} config
   * @param {function} config.listener The function which needs to be called on
   *   a schedule.
   * @param {function} config.getNextTimestamp A function which returns the
   *   timestamp that the listener should next be called.
   * @param {number} config.interval After a successful listener call, wait this
   *   long before the next poll.
   * @param {number} config.errorRetryDelay After a failed listener call, wait
   *   this long before the next poll.
   */
  constructor(config) {
    this.config = config;
    this.checkInProgress = false;
    this.stopped = false;

    this._check = async() => {
      this.checkInProgress = true;
      let nextPollTimeout;
      try {
        nextPollTimeout = await this._callListenerIfItsTime();
      }
      finally {
        if (typeof nextPollTimeout == "undefined") {
          nextPollTimeout = this.config.errorRetryDelay;
        }

        if (nextPollTimeout > MAX_POLL_TIMEOUT) {
          nextPollTimeout = MAX_POLL_TIMEOUT;
        }

        // If stop was called while currently triggering something, don't
        // schedule the next timeout.
        if (!this.stopped) {
          this.timeout = setTimeout(this._check, nextPollTimeout);
        }
        this.checkInProgress = false;
      }
    };
    this._check();
  }

  /**
   * @ignore
   */
  async _callListenerIfItsTime() {
    let nextTimestamp = await this.config.getNextTimestamp();
    let nowTimestamp = Date.now();
    if (nextTimestamp && nowTimestamp < nextTimestamp) {
      return nextTimestamp - nowTimestamp;
    }

    let success = await this.config.listener();
    if (!success) {
      return this.config.errorRetryDelay;
    }

    return this.config.interval;
  }

  /**
   * Stops the scheduler.
   */
  stop() {
    if (this.stopped) {
      return;
    }

    this.stopped = true;
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  /**
   * Calls `getNextTimestamp` immediately to reschedule the next call. Call this
   * function if anything has changed that could affect when the listener should
   * next be called.
   */
  checkNow() {
    if (!this.stopped && !this.checkInProgress) {
      clearTimeout(this.timeout);
      // The promise returned by _check() is deliberately not awaited
      // here. We're kicking off a check now, not propagating the results of
      // this schedule check up to the caller.
      this._check();
    }
  }
}

/**
 * Returns the next timestamp when the scheduler should check for updates.
 * If there was a network error and the clock was set backwards, an updated
 * lastNetworkError timestamp is returned too.
 *
 * @param {string} lastSuccessfulRequest
 *   ISO Date of the last successful download
 * @param {string} lastRequestError
 *   ISO Date of the last request error, if any
 * @param {number} errorRetryDelay
 *   Delay in milliseconds to wait after a network error before retrying
 * @param {number} syncInterval Interval in milliseconds to wait after
 *   a successful download before checking again
 * @return {Object} An object containing the next check timestamp
 *   and optionally an updated lastRequestError timestamp
 */
export function getNextRequestTimestamp({
  lastSuccessfulRequest,
  lastRequestError,
  errorRetryDelay,
  syncInterval
}) {
  if (lastRequestError) {
    let nextRetryAfterError = new Date(lastRequestError).getTime() +
      errorRetryDelay;
    let nowTimestamp = Date.now();
    let maxNextRetry = nowTimestamp + errorRetryDelay;
    if (nextRetryAfterError > maxNextRetry) {
      // This shouldn't happen if the clocks are working right. It implies
      // that the system clock has been set backwards since we stored that
      // timestamp. Let's correct the storage using our current clock
      // value. We shouldn't do this with lastPing because lastPing comes
      // from the server.
      return {
        lastRequestError: new Date(nowTimestamp).toISOString(),
        nextCheckTimestamp: maxNextRetry
      };
    }

    return {
      nextCheckTimestamp: nextRetryAfterError
    };
  }
  else if (lastSuccessfulRequest) {
    const nextCheckTimestamp = new Date(lastSuccessfulRequest).getTime() +
      syncInterval;

    return {
      nextCheckTimestamp
    };
  }

  return {nextCheckTimestamp: null};
}
